import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Typography,
  TextareaAutosize,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import ConfirmationPopup from './ConfirmationPopup';

type InsertPopupProps = {
  open: boolean;
  actionInitiated: string;
  onClose: () => void;
  onInsert: (data: InsertFormData) => void;
  hasError: boolean;
  isActionPending: boolean;
};

type InsertFormData = {
  providerNPI: string;
  providerTaxID: string;
  recommendation: string;
  externalNotes: string;
  internalNotes: string;
  active: boolean;
};

const useStyles = makeStyles(theme => ({
  flexBox: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  select: {
    '& .MuiSelect-selectMenu': {
      background: '#ffffff',
    },
    '& .MuiSelect-selectMenu:focus': {
      border: 'none',
    },
  },
  textField: {
    width: '100%',
    marginLeft: theme.spacing(1),
    '& .MuiInputLabel-formControl': {
      paddingLeft: '.5rem',
    },
    '& .MuiInputBase-fullWidth': {
      width: '103% !important',
    },
  },
  header: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    fontWeight: 600,
    fontSize: '1.4rem',
  },
  label: {
    marginRight: theme.spacing(2),
    minWidth: '10rem',
    display: 'inline-block',
    textAlign: 'right',
    fontWeight: 600,
  },
  textarea: {
    width: '92%',
    padding: '10px 14px',
    borderRadius: 4,
    border: '1px solid #E5EBF5',
    resize: 'none',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    lineHeight: 'inherit',
  },
  cancelButton: {
    color: '#ffffff',
    backgroundColor: theme.palette.error.main,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
  insertButton: {
    color: '#ffffff',
    backgroundColor: theme.palette.success.main,
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
    },
  },
  customDialog: {
    '& .MuiDialog-container ': {
      '& .MuiDialog-paperWidthSm': {
        maxWidth: '700px !important',
        '& .MuiDialogContent-root': {
          padding: '8px 40px !important',
          overflowY: 'none',
        },
      },
    },
  },
  errorText: {
    color: 'red',
    marginTop: theme.spacing(1),
  },
}));

const CreateProviderSearch: React.FC<InsertPopupProps> = ({
  hasError,
  actionInitiated,
  isActionPending,
  open,
  onClose,
  onInsert,
}) => {
  const classes = useStyles();
  const [formData, setFormData] = useState<InsertFormData>({
    providerNPI: '',
    providerTaxID: '',
    recommendation: '',
    externalNotes: '',
    internalNotes: '',
    active: true,
  });
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [errors, setErrors] = useState<{ [key in keyof InsertFormData]?: string }>({});

  const validateForm = () => {
    const newErrors: { [key in keyof InsertFormData]?: string } = {};
    if (!formData.providerNPI.trim()) {
      newErrors.providerNPI = 'Provider NPI is required';
    } else if (!/^\d{10}$/.test(formData.providerNPI)) {
      newErrors.providerNPI = 'Provider NPI must be a 10-digit number';
    }
    if (!formData.recommendation.trim()) {
      newErrors.recommendation = 'Recommendation is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | { name?: string; value: unknown }>
  ) => {
    const { name, value } = e.target;

    if (name === 'providerNPI') {
      // Ensure only numbers and max length of 10 for NPI
      const numericValue = value as string;
      if (/^\d*$/.test(numericValue) && numericValue.length <= 10) {
        setFormData({
          ...formData,
          [name]: numericValue,
        });
      }
    } else if (name === 'providerTaxID') {
      // Allow only numbers, max length 9
      const numericValue = value as string;
      if (/^\d*$/.test(numericValue) && numericValue.length <= 9) {
        setFormData({
          ...formData,
          [name]: numericValue,
        });
      }
    } else {
      setFormData({
        ...formData,
        [name as string]: value,
      });
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, name } = e.target;
    setFormData({
      ...formData,
      [name as string]: checked,
    });
  };

  const handleInsert = () => {
    if (validateForm()) {
      onInsert(formData);
    }
  };

  const handleClose = () => {
    setOpenConfirmation(false);
  };

  const handleCancel = () => {
    resetFormData();
    onClose();
  };

  const resetFormData = () => {
    setFormData({
      providerNPI: '',
      providerTaxID: '',
      recommendation: '',
      externalNotes: '',
      internalNotes: '',
      active: true,
    });
  };

  useEffect(() => {
    if (!isActionPending && !hasError && actionInitiated === 'insert') {
      onClose();
      setOpenConfirmation(true);
    }
  }, [isActionPending]);

  useEffect(() => {
    setOpenConfirmation(false);
  }, []);

  useEffect(() => {
    if (!open) {
      resetFormData();
    }
  }, [open]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby="insert-popup-title"
        className={classes.customDialog}
      >
        <Typography className={classes.header}>Insert New Record</Typography>
        <DialogContent>
          <div className={classes.flexBox}>
            <Typography className={classes.label}>
              Provider NPI<span style={{ color: 'red' }}>&nbsp;*</span>
            </Typography>
            <FormControl fullWidth className={classes.textField}>
              <TextField
                required
                name="providerNPI"
                label="Required"
                value={formData.providerNPI}
                onChange={handleInputChange}
                margin="normal"
                // eslint-disable-next-line react/jsx-no-duplicate-props
                InputProps={{ className: classes.textField }}
                // eslint-disable-next-line react/jsx-no-duplicate-props
                inputProps={{ maxLength: 10 }}
                error={!!errors.providerNPI}
              />
              {errors.providerNPI && (
                <Typography variant="caption" className={classes.errorText}>
                  {errors.providerNPI}
                </Typography>
              )}
            </FormControl>
          </div>
          <div className={classes.flexBox}>
            <Typography className={classes.label}>Provider Tax ID</Typography>
            <FormControl fullWidth className={classes.textField}>
              <TextField
                name="providerTaxID"
                label="Optional"
                value={formData.providerTaxID}
                onChange={handleInputChange}
                margin="normal"
                InputProps={{ className: classes.textField }}
              />
            </FormControl>
          </div>
          <div className={classes.flexBox}>
            <Typography className={classes.label}>
              Recommendation<span style={{ color: 'red' }}>&nbsp;*</span>
            </Typography>
            <FormControl fullWidth className={classes.textField}>
              <Select
                name="recommendation"
                value={formData.recommendation}
                onChange={handleInputChange}
                className={classes.select}
                fullWidth
                error={!!errors.recommendation}
              >
                <MenuItem value="CAUTION">Caution</MenuItem>
                <MenuItem value="UNFRIENDLY">Unfriendly</MenuItem>
                <MenuItem value="DELISTED">Delisted</MenuItem>
              </Select>
              {errors.recommendation && (
                <Typography variant="caption" className={classes.errorText}>
                  {errors.recommendation}
                </Typography>
              )}
            </FormControl>
          </div>
          <div className={classes.flexBox}>
            <Typography className={classes.label}>External Notes</Typography>
            <FormControl fullWidth className={classes.textField}>
              <TextareaAutosize
                minRows={4}
                name="externalNotes"
                placeholder="Optional"
                value={formData.externalNotes}
                onChange={handleInputChange}
                className={classes.textarea}
              />
            </FormControl>
          </div>
          <div className={classes.flexBox}>
            <Typography className={classes.label}>Internal Notes</Typography>
            <FormControl fullWidth className={classes.textField}>
              <TextareaAutosize
                minRows={4}
                name="internalNotes"
                placeholder="Optional"
                value={formData.internalNotes}
                onChange={handleInputChange}
                className={classes.textarea}
              />
            </FormControl>
          </div>
          <div className={classes.flexBox}>
            <Typography className={classes.label}>Active</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.active}
                  onChange={handleCheckboxChange}
                  name="active"
                  color="primary"
                />
              }
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} className={classes.cancelButton}>
            Cancel
          </Button>
          <Button onClick={handleInsert} className={classes.insertButton}>
            Insert
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationPopup
        message="Record(s) Successfully Added"
        open={openConfirmation && !hasError}
        onClose={handleClose}
      />
    </>
  );
};

export default CreateProviderSearch;
